import { useQuery } from 'react-query';

import { customInstance } from '@ping/api/custom-instance';
import { governanceListParams } from './governance-list-query.utils';
import type { IGovernanceListQuery, IGovernancePaging } from './governance-list-query.utils';

const governanceApi = '/frontoffice/api/governance';

const get = <T>(url: string, params?: Record<string, string | number>) =>
  customInstance<T>({ url, method: 'GET', params });

export interface IGovernancePage<T> {
  data: T[];
  paging: IGovernancePaging;
}

export interface IGovernanceRequestSummary {
  id: string;
  actionType: string;
  status: string;
  statusLabel: string;
  phase: string;
  requesterCorePassId: string;
  belongsToCurrentMember: boolean;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string | null;
}

export interface IGovernanceRequestDetail extends IGovernanceRequestSummary {
  consequence: string;
  documentNote: string;
  failureReason?: string | null;
}

export interface IGovernanceAuditEvent {
  id: string;
  sequence: number;
  occurredAt: string;
  governanceRequestId?: string | null;
  eventType: string;
  summary: string;
  actorCorePassId?: string | null;
  resultingStatusLabel?: string | null;
}

export interface IVotingMember {
  memberName: string;
  cnsDomain: string;
}

const listOptions = { keepPreviousData: true };

export const withdrawalRequestsQueryKey = ['business-withdrawal-requests'] as const;
export const pendingApprovalsQueryKey = ['business-approvals-pending'] as const;
export const approvalHistoryQueryKey = ['business-approvals-history'] as const;
export const governanceAuditQueryKey = ['business-governance-audit'] as const;
export const governanceRequestQueryKey = ['business-governance-request'] as const;

export const useWithdrawalRequests = (query: IGovernanceListQuery) =>
  useQuery(
    [...withdrawalRequestsQueryKey, query],
    () => get<IGovernancePage<IGovernanceRequestSummary>>(`${governanceApi}/withdrawals`, governanceListParams(query)),
    listOptions
  );

export const usePendingApprovals = (query: IGovernanceListQuery) =>
  useQuery(
    [...pendingApprovalsQueryKey, query],
    () =>
      get<IGovernancePage<IGovernanceRequestSummary>>(
        `${governanceApi}/approvals/pending`,
        governanceListParams(query)
      ),
    listOptions
  );

export const useApprovalHistory = (query: IGovernanceListQuery) =>
  useQuery(
    [...approvalHistoryQueryKey, query],
    () =>
      get<IGovernancePage<IGovernanceRequestSummary>>(
        `${governanceApi}/approvals/history`,
        governanceListParams(query)
      ),
    listOptions
  );

export const useGovernanceAudit = (query: IGovernanceListQuery) =>
  useQuery(
    [...governanceAuditQueryKey, query],
    () => get<IGovernancePage<IGovernanceAuditEvent>>(`${governanceApi}/audit`, governanceListParams(query)),
    listOptions
  );

export const useVotingMembers = (enabled: boolean) =>
  useQuery(['business-voting-members'], () => get<IVotingMember[]>(`${governanceApi}/voting-members`), { enabled });

export const useGovernanceRequest = (requestId?: string) =>
  useQuery(
    [...governanceRequestQueryKey, requestId],
    () => get<IGovernanceRequestDetail>(`${governanceApi}/requests/${requestId}`),
    { enabled: Boolean(requestId) }
  );
